import React, { useMemo, useState } from "react";
import "../components/App.css";
import { Container } from "@mui/material";
import { useActiveCareUser } from "../hooks/useActiveCareUser";
import { API_BASE_URL } from "../config/config";

const Payments = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [downloadingServiceId, setDownloadingServiceId] = useState(null);
  const { userDetails } = useActiveCareUser();

  const filteredServices = useMemo(() => {
    if (!userDetails?.services || !selectedService) return [];

    const [selectedTypeCategory, selectedId] = selectedService.split(" - ");
    const [selectedType, selectedCategory] = selectedTypeCategory.split(" (");

    if (selectedId) {
      return userDetails.services.filter(
        (service) =>
          service.type === selectedType &&
          service.category === selectedCategory.slice(0, -1) &&
          service._id === selectedId
      );
    }

    if (selectedCategory) {
      return userDetails.services.filter(
        (service) =>
          service.type === selectedType &&
          service.category === selectedCategory.slice(0, -1)
      );
    }

    return userDetails.services.filter(
      (service) => service.type === selectedType
    );
  }, [selectedService, userDetails]);

  const handleServiceSelect = (event) => {
    setSelectedService(event.target.value);
  };

  const serviceTypeRadioButtons = (
    <div className="service-type-radio-buttons">
      <h3>Choose a service to get the payment details.</h3>
      {userDetails?.services?.map((service) => {
        const countSameTypeCategory = userDetails.services.filter(
          (s) => s.type === service.type && s.category === service.category
        ).length;
        const label =
          countSameTypeCategory > 1
            ? `${service.type} (${service.category}) - ${service._id}`
            : `${service.type} (${service.category})`;
        return (
          <label key={label} className="radio-label">
            <input
              type="radio"
              value={label}
              style={{ marginRight: "5px" }}
              checked={selectedService === label}
              onChange={handleServiceSelect}
            />
            {label}
          </label>
        );
      })}
    </div>
  );

  const hasAgreement = (service) =>
    service?.payments?.some(
      (payment) => payment.paymentStatus === "paid" && payment.agreementUrl
    );

  const handleAgreementDownload = async (serviceId) => {
    try {
      setDownloadingServiceId(serviceId);
      const token = localStorage.getItem("care_token");
      const response = await fetch(
        `${API_BASE_URL}/agreement/download/${serviceId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bevogtet-protection-care-agreement.pdf";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading agreement:", error);
    } finally {
      setDownloadingServiceId(null);
    }
  };

  const serviceDetails =
    selectedService &&
    filteredServices.map((service, index) => (
      <div key={index}>
        <div className="user-container" style={{ margin: "0 auto" }}>
          <h2 style={{ textAlign: "center" }}>Payment Details</h2>
          <div className="user-details">
            <div className="space-between">
              <p>
                <strong>Care Fees:</strong>
              </p>
              <p>{service.careFees}</p>
            </div>
            {service.careExpiryDate && service.careExpiryDate !== "" && (
              <div className="space-between">
                <p>
                  <strong>Expires On:</strong>
                </p>
                <p>{service.careExpiryDate}</p>
              </div>
            )}
            <div className="space-between">
              <p>
                <strong>Download Invoice:</strong>
              </p>
              <p>PDF</p>
            </div>
            <div className="space-between">
              <p>
                <strong>Download Bevogtet Agreement:</strong>
              </p>
              {hasAgreement(service) ? (
                <p>
                  <button
                    type="button"
                    className="redirect-button"
                    onClick={() => handleAgreementDownload(service._id)}
                    disabled={downloadingServiceId === service._id}
                  >
                    {downloadingServiceId === service._id
                      ? "Downloading..."
                      : "Download PDF"}
                  </button>
                </p>
              ) : (
                <p>Not available</p>
              )}
            </div>
            <div className="space-between">
              <p>
                <strong>Payment Mode:</strong>
              </p>
              <p>Card</p>
            </div>
            {service.invoice && service.invoice !== "" && (
              <div className="space-between">
                <p>
                  <strong>Uploaded Copy of Product Bill:</strong>
                </p>
                <p>
                  <a href={service.invoice} target="_blank" rel="noreferrer">
                    View
                  </a>
                </p>
              </div>
            )}
            {service.selfie && service.selfie !== "" && (
              <div className="space-between">
                <p>
                  <strong>Uploaded Selfie:</strong>
                </p>
                <p>
                  <a href={service.selfie} target="_blank" rel="noreferrer">
                    View
                  </a>
                </p>
              </div>
            )}
            {service.registrationDetails &&
              service.registrationDetails !== "" && (
                <div className="space-between">
                  <p>
                    <strong>Uploaded Registration Details:</strong>
                  </p>
                  <p>
                    <a
                      href={service.registrationDetails}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View
                    </a>
                  </p>
                </div>
              )}
            {service.eventInvite && service.eventInvite !== "" && (
              <div className="space-between">
                <p>
                  <strong>Uploaded Event Invite:</strong>
                </p>
                <p>
                  <a
                    href={service.eventInvite}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View
                  </a>
                </p>
              </div>
            )}
            {service.pictures && service.pictures.length > 0 && (
              <div className="space-between">
                <p>
                  <strong>Uploaded Pictures:</strong>
                </p>
                <p>
                  <a href={service.pictures} target="_blank" rel="noreferrer">
                    View
                  </a>
                </p>
              </div>
            )}
            {service.establishmentPictures &&
              service.establishmentPictures.length > 0 && (
                <div className="space-between">
                  <p>
                    <strong>Uploaded Pictures:</strong>
                  </p>
                  <p>
                    <a
                      href={service.establishmentPictures}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View
                    </a>
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
    ));

  return (
    <Container>
      <div className="care-container">
          {serviceTypeRadioButtons}
          {serviceDetails}
        </div>
    </Container>
  );
};

export default Payments;
